import type { Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import { useSessionContext } from '@/contexts/session/context';
import type { ChatMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { ChatMessagePayloadType } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { ChatMessageStatus } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { useChatContext } from '@/contexts/chat/context';
import SysText from '../../sysText';
import VoiceIcon from './images/voice.svg?react';
import VideoIcon from './images/video.svg?react';
import styles from './index.module.scss';

const formatDuration = (ms: number): string => {
  if (ms < 0) return '0s';

  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds === 0) return '0s';

  const hours = Math.floor(totalSeconds / 3600);
  const remainingSeconds = totalSeconds % 3600;
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  const pad = (n: number) => n.toString().padStart(2, '0');
  const parts = [];

  if (hours > 0) parts.push(`${hours}h`);
  if (hours > 0 || minutes > 0) parts.push(`${hours > 0 ? pad(minutes) : minutes}m`);
  parts.push(`${pad(seconds)}s`);

  return parts.join(' ');
};

const CallMessage = memo(
  ({
    payload_type,
    noStyle,
    status,
    chat,
    message,
    showIcon = true,
  }: {
    payload?: string;
    payload_type: ChatMessagePayloadType;
    status: ChatMessageStatus;
    chat: Chat;
    message: ChatMessage;
    noStyle?: boolean;
    showIcon?: boolean;
  }) => {
    const {
      session: { user },
    } = useSessionContext();
    const { chatMediaCall } = useChatContext();

    const userName = useMemo(() => {
      if (message.owner_id === user?.userId) {
        return user?.name;
      } else {
        return chat.user.name;
      }
    }, [chat.user.name, message.owner_id, user]);

    const userName_ = useMemo(() => {
      if (message.owner_id === user?.userId) {
        return chat.user.name;
      } else {
        return user?.name;
      }
    }, [chat.user.name, message.owner_id, user]);

    const time = useMemo(() => {
      if (message.update_at && message.create_at) {
        return formatDuration(message.update_at - message.create_at);
      }
      return '';
    }, [message]);

    const status_ = chatMediaCall.getMediaStatus(
      chatMediaCall.chatMediaInfo,
      message,
      status,
      chat
    );

    const isError = useMemo(() => {
      return (
        status_ === ChatMessageStatus.CALL_TIMEOUT ||
        status_ === ChatMessageStatus.CALL_CANCELLED ||
        status_ === ChatMessageStatus.CALL_REJECTED
        // status_ === ChatMessageStatus.CALL_BUSY ||
        // status_ === ChatMessageStatus.CALL_NETWORK_ERROR
      );
    }, [status_]);

    return (
      <SysText noStyle={noStyle}>
        {showIcon && (
          <span className={cls(styles.type, isError && styles.error)}>
            {payload_type === ChatMessagePayloadType.MEDIA_AUDIO_CALL ? (
              <VoiceIcon />
            ) : (
              <VideoIcon />
            )}
          </span>
        )}
        <Trans
          components={{
            name: <a />,
          }}
          values={{
            name: status_ === ChatMessageStatus.CALL_TIMEOUT ? userName_ : userName,
            time,
          }}
          i18nKey={`messages.chat.message.media.${payload_type}.${status_}`}
        />
      </SysText>
    );
  }
);

export default CallMessage;
